import { z } from "zod";
import { IanaTimezoneSchema } from "./timezone.schema";

const IsoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format");

const FUTURE_DATE_TOLERANCE_DAYS = 1;

function maxAllowedDate(): string {
  const limit = new Date();
  limit.setUTCDate(limit.getUTCDate() + FUTURE_DATE_TOLERANCE_DAYS);
  return limit.toISOString().slice(0, 10);
}

export const RequestListQuerySchema = z
  .strictObject({
    dateFrom: IsoDateSchema,
    dateTo: IsoDateSchema,
    search: z.string().max(100).optional(),
    status: z.string().max(100).optional(),
    consumer: z.string().max(100).optional(),
    requestType: z.string().max(100).optional(),
    documentType: z.string().max(100).optional(),
    model: z.string().max(100).optional(),
    humanReview: z
      .enum(["true", "false"])
      .optional()
      .transform((value) => (value === undefined ? undefined : value === "true")),
    page: z.coerce.number().int().min(1).optional().default(1),
    pageSize: z.coerce.number().int().min(1).max(100).optional().default(30),
    timezone: IanaTimezoneSchema,
  })
  .refine((value) => value.dateFrom <= value.dateTo, {
    message: "dateFrom must not be after dateTo",
    path: ["dateFrom"],
  })
  .refine((value) => value.dateTo <= maxAllowedDate(), {
    message: "dateTo must not be in the future",
    path: ["dateTo"],
  });

export const RequestListItemSchema = z.object({
  requestId: z.string().uuid(),
  fileName: z.string().nullable(),
  thumbnailUrl: z.string().nullable(),
  fileUrl: z.string().nullable(),
  fileSizeBytes: z.number().nullable(),
  fileKind: z.string().nullable(),
  requestTypeKey: z.string(),
  consumerKey: z.string(),
  consumerName: z.string(),
  statusKey: z.string().nullable(),
  statusName: z.string().nullable(),
  humanReview: z.boolean(),
  documentTypeKey: z.string().nullable(),
  totalPages: z.number().nullable(),
  durationMs: z.number().nullable(),
  submittedAt: z.string(),
  inputTokens: z.number().nullable(),
  outputTokens: z.number().nullable(),
  inputCost: z.number().nullable(),
  outputCost: z.number().nullable(),
  totalCost: z.number().nullable(),
  modelProvider: z.string().nullable(),
  modelName: z.string().nullable(),
  modelAccentColor: z.string().nullable(),
  additionalModelCount: z.number().int().nonnegative(),
  additionalModelNames: z.array(z.string()).default([]),
});

export const RequestListDataSchema = z.object({
  rows: z.array(RequestListItemSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});

export type RequestListQuery = z.infer<typeof RequestListQuerySchema>;
export type RequestListItem = z.infer<typeof RequestListItemSchema>;
export type RequestListData = z.infer<typeof RequestListDataSchema>;
